import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import html2pdf from 'html2pdf.js';
import JSZip from 'jszip';
import QRCode from 'qrcode';
import { Plus, Clock, Trash2, Calendar, Download, Eye, X, CheckSquare, Square, FileBadge, Car, Globe, Link2, MessageCircle, QrCode, Check } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { supabase } from '../../../config/supabase';
import { formatDateFR, getTimestamp, sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import RentalForm from './RentalForm';
import DocumentViewerModal from '../../shared/components/DocumentViewerModal';
import { printMokhalafaNarsa } from '../../mokhalafat/utils/printNarsa';
import { carDamageCompactHtml } from '../../shared/utils/carDamage';
import { generateContractDoc } from '../utils/contractDoc';
import { enforceDriver2 } from '../utils/rentalValidation';
import BlacklistWarningModal from '../../blacklist/components/BlacklistWarningModal';
import { matchClient, logAudit } from '../../blacklist/utils/blacklistService';
import { reconcileRentalCommission } from '../../staff/utils/commissionService';
import { useAuth } from '../../../context/AuthContext';

export default function RentalsPage({ settings }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const { agencyId } = useAuth();

  const { data: rentals, add: addRental, update: updateRental, remove: removeRental, loading: rentalsLoading } = useCollection('rentals');
  const { data: clients, update: updateClient, loading: clientsLoading } = useCollection('clients');
  const { data: cars, update: updateCar, loading: carsLoading } = useCollection('cars');
  const { add: addPayment } = useCollection('client_payments');
  const { data: mokhalafat } = useCollection('mokhalafat');
  // Staff + commissions (additive; drives the per-rental commission engine).
  const { data: staffList } = useCollection('staff');
  const { data: commissions, add: addCommission, update: updateCommission, remove: removeCommission } = useCollection('staff_commissions');
  const commissionOps = { add: addCommission, update: updateCommission, remove: removeCommission };
  // Global blacklist (shared by all agencies, realtime, read-only here).
  const { data: blacklist } = useCollection('blacklist', { bypassAgencyFilter: true });

  const [blCheck, setBlCheck] = useState(null); // { matches, payload }
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [viewDoc, setViewDoc] = useState(null);
  const [downloadDocsState, setDownloadDocsState] = useState(null);
  const [signShareState, setSignShareState] = useState(null);
  const [selectedRentalIds, setSelectedRentalIds] = useState([]);
  const [, forceTick] = useState(0);

  // Refresh open contract calculations every hour
  useEffect(() => {
    const interval = setInterval(() => forceTick(t => t + 1), 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Clear selected records when table filter changes
  useEffect(() => {
    setSelectedRentalIds([]);
  }, [filter]);

  // Compute live values for open contracts
  const computeLiveRental = (r) => {
    if (!r.isOpenContract || !r.startDate || r.status !== 'active') return r;
    const startTime = new Date(r.startDate).getTime();
    const diffMs = Date.now() - startTime;
    const liveDays = Math.max(1, Math.ceil(diffMs / 86400000));
    const liveTotal = liveDays * (r.dailyRate || 0);
    return { ...r, days: liveDays, totalAmount: liveTotal, _isLive: true };
  };

  const enrichedRentals = useMemo(() => {
    return rentals.map(computeLiveRental);
  }, [rentals]);

  const filtered = useMemo(() => {
    if (filter === 'all') return enrichedRentals;
    return enrichedRentals.filter(r => r.status === filter);
  }, [enrichedRentals, filter]);

  const handleStatusChange = async (id, newStatus) => {
    const rental = rentals.find(r => r.id === id);
    if (!rental) return;
    
    // Already in target status? Don't re-process
    if (rental.status === newStatus) return;
    
    const updateData = { status: newStatus };
    
    // If completing an OPEN CONTRACT, freeze the final amount/days
    if (newStatus === 'completed' && rental.isOpenContract) {
      const startTime = new Date(rental.startDate).getTime();
      const diffMs = Date.now() - startTime;
      const finalDays = Math.max(1, Math.ceil(diffMs / 86400000));
      const finalTotal = finalDays * (rental.dailyRate || 0);
      const finalEndDate = new Date().toISOString().split('T')[0];
      
      updateData.days = finalDays;
      updateData.totalAmount = finalTotal;
      updateData.endDate = finalEndDate;
      updateData.isOpenContract = false;
      updateData.closedFromOpen = true;
    }
    
    // Mark as completed but NOT paid by default
    if (newStatus === 'completed' && rental.status !== 'completed') {
      updateData.isPaid = false;
      updateData.completedAt = new Date().toISOString();
    }
    
    try {
      await updateRental(id, updateData);

      // Commission sync on status change (removed on cancel, kept otherwise).
      try {
        await reconcileRentalCommission({
          rental: { ...rental, ...updateData },
          staffList, commissions, ops: commissionOps,
        });
      } catch (_) {}

      if (newStatus === 'completed' && rental.status !== 'completed') {
        const car = cars.find(c => c.id === rental.carId);
        if (car) {
          const otherActive = rentals.some(r => r.carId === car.id && r.id !== id && r.status === 'active');
          const nextCarStatus = otherActive ? 'rented' : 'available';
          await updateCar(car.id, {
            status: nextCarStatus, 
            totalRented: (car.totalRented || 0) + 1
          });
        }
        
        // KEY FIX: ADD AMOUNT TO CLIENT'S CREDIT (debt)
        const finalTotal = updateData.totalAmount || rental.totalAmount || 0;
        const deposit = parseFloat(rental.deposit) || 0;
        const amountDue = finalTotal - deposit;
        
        if (amountDue > 0 && rental.clientId) {
          const client = clients.find(c => c.id === rental.clientId);
          if (client) {
            const currentBalance = client.creditBalance || 0;
            const newBalance = currentBalance + amountDue;
            
            await updateClient(client.id, {
              creditBalance: newBalance
            });
            
            // Log this as a "rental_completion" entry (negative amount = debt added)
            await addPayment({
              clientId: rental.clientId,
              clientName: rental.clientName,
              amount: -amountDue,
              previousBalance: currentBalance,
              newBalance: newBalance,
              paymentMethod: 'Location',
              note: (isAr 
                ? `🚗 إيجار: ${rental.carName} (${updateData.days || rental.days} يوم)` 
                : `🚗 Location: ${rental.carName} (${updateData.days || rental.days} jours)`) 
                + (deposit > 0 ? ` - Acompte: ${deposit}` : ''),
              date: new Date().toISOString().split('T')[0],
              timestamp: new Date().toISOString(),
              createdBy: 'auto_rental_completion',
              rentalId: id,
              isDebit: true
            });
          }
        }
        
        const finalDaysMsg = updateData.days || rental.days;
        const finalTotalMsg = updateData.totalAmount || rental.totalAmount;
        const openTag = rental.isOpenContract ? ' (Contrat ouvert clôturé ⏳)' : '';
        
        await sendTelegramNotification(
          buildTelegramMessage('rental_completed', {
            clientName: rental.clientName, carName: rental.carName,
            days: finalDaysMsg, total: finalTotalMsg, currency: settings.currency,
            creditAdded: amountDue, isOpenClosed: !!rental.isOpenContract
          }),
          'rental_completed'
        );
      }

      // When a rental is cancelled, free the car back to available if no other active rentals
      if (newStatus === 'cancelled' && rental.status === 'active') {
        const car = cars.find(c => c.id === rental.carId);
        if (car && car.status === 'rented') {
          const otherActive = rentals.some(r => r.carId === car.id && r.id !== id && r.status === 'active');
          if (!otherActive) {
            await updateCar(car.id, { status: 'available' });
          }
        }
      }

      // When a rental status is set to active, ensure the car status is updated to rented
      if (newStatus === 'active') {
        const car = cars.find(c => c.id === rental.carId);
        if (car && car.status !== 'rented') {
          await updateCar(car.id, { status: 'rented' });
        }
      }
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const printContract = (rOriginal, action = 'print') => generateContractDoc(rOriginal, { settings, cars, isAr, action });

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete_rental') || (isAr ? 'هل تريد حذف هذا العقد؟' : 'Supprimer cette location?'))) {
      try {
        const rental = rentals.find(r => r.id === id);
        if (rental?.carId) {
          const car = cars.find(c => c.id === rental.carId);
          if (car && car.status === 'rented') {
            const otherActive = rentals.some(r => r.carId === rental.carId && r.id !== id && r.status === 'active');
            if (!otherActive) {
              await updateCar(car.id, { status: 'available' });
            }
          }
        }
        await removeRental(id);
        // Remove any commission tied to this deleted rental.
        try {
          await reconcileRentalCommission({
            rental: { id, staffId: rental?.staffId, status: rental?.status },
            deleted: true, staffList, commissions, ops: commissionOps,
          });
        } catch (_) {}
        setSelectedRentalIds(prev => prev.filter(selectedId => selectedId !== id));
      } catch (err) {
        alert('❌ Erreur: ' + err.message);
      }
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedRentalIds.length;
    const confirmMsg = isAr 
      ? `هل أنت متأكد من حذف ${count} عقود كراء محددة نهائياً؟` 
      : `Voulez-vous vraiment supprimer définitivement les ${count} contrats sélectionnés ?`;
      
    if (window.confirm(confirmMsg)) {
      try {
        const carIdsToCheck = new Set();
        for (const id of selectedRentalIds) {
          const rental = rentals.find(r => r.id === id);
          if (rental?.carId) {
            carIdsToCheck.add(rental.carId);
          }
        }

        await removeRental(selectedRentalIds);

        // Remove commissions for all deleted rentals.
        try {
          for (const delId of selectedRentalIds) {
            await reconcileRentalCommission({
              rental: { id: delId }, deleted: true, staffList, commissions, ops: commissionOps,
            });
          }
        } catch (_) {}

        for (const carId of carIdsToCheck) {
          const car = cars.find(c => c.id === carId);
          if (car && car.status === 'rented') {
            const otherActive = rentals.some(r => 
              r.carId === carId && 
              !selectedRentalIds.includes(r.id) && 
              r.status === 'active'
            );
            if (!otherActive) {
              await updateCar(car.id, { status: 'available' });
            }
          }
        }

        setSelectedRentalIds([]);
        alert(isAr ? 'تم حذف العقود بنجاح' : 'Contrats supprimés avec succès.');
      } catch (err) {
        alert('❌ Erreur: ' + err.message);
      }
    }
  };

  // Actual persistence (runs after blacklist verification passes / is overridden).
  const doSaveRental = async (payload) => {
    try {
      // Back-end guard: clear/validate second-driver data right before the DB
      // write so no invalid 2nd-driver data can ever be persisted.
      const safePayload = enforceDriver2(payload, isAr);

      const inserted = await addRental(safePayload);
      const newRentalId = inserted?.id || inserted?._id;
      // Mark car as rented
      await updateCar(safePayload.carId, { status: 'rented' });

      // Commission: create for the assigned employee (idempotent, dup-safe).
      if (newRentalId) {
        try {
          await reconcileRentalCommission({
            rental: { ...safePayload, id: newRentalId },
            staffList, commissions, ops: commissionOps,
          });
        } catch (_) {}
      }

      await sendTelegramNotification(
        buildTelegramMessage('new_rental', {
          clientName: payload.clientName, carName: payload.carName,
          startDate: payload.startDate, endDate: payload.endDate,
          isOpen: !!payload.isOpenContract,
          amount: payload.totalAmount, currency: settings.currency
        }),
        'new_rental'
      );
    } catch (err) {
      alert('❌ Erreur: ' + err.message);
    }
  };

  const handleSave = async (payload) => {
    // Automatic global-blacklist verification before creating the contract.
    try {
      const matches = matchClient(payload, blacklist || []);
      if (matches.length) {
        logAudit('viewed', { actor: agencyId, agencyId, targetName: payload.clientName, details: { context: 'contract_check', matched: matches.length } });
        setBlCheck({ matches, payload });   // show warning; save is deferred
        return;
      }
    } catch (_) { /* verification must never block a clean client */ }
    await doSaveRental(payload);
  };

  if (rentalsLoading || clientsLoading || carsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div>
      {blCheck && (
        <BlacklistWarningModal
          matches={blCheck.matches}
          onCancel={() => {
            logAudit('rejection', { actor: agencyId, agencyId, targetName: blCheck.payload?.clientName, details: { context: 'contract_cancelled' } });
            setBlCheck(null);
          }}
          onContinue={async () => {
            const { payload, matches } = blCheck;
            await logAudit('override', {
              actor: agencyId, agencyId, targetName: payload?.clientName,
              targetId: matches[0]?.record?.id || '',
              details: { context: 'contract_override', matchedReasons: matches.map(m => m.reasons).flat(), risk: matches[0]?.record?.riskLevel },
            });
            setBlCheck(null);
            setShowForm(false);
            await doSaveRental(payload);
          }}
        />
      )}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-gray-900">{t('rentals_title')}</h1>
        <button 
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow transition-all duration-200"
        >
          <Plus className="w-5 h-5" /> 
          {t('new_rental')}
        </button>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 max-w-full scrollbar-thin">
        {[
          { v: 'all', l: t('filter_all') || (isAr ? 'الكل' : 'Tous') },
          { v: 'active', l: t('filter_active') || (isAr ? 'نشط' : 'Actif') },
          { v: 'completed', l: t('filter_completed') || (isAr ? 'مكتمل' : 'Terminé') },
          { v: 'cancelled', l: t('filter_cancelled') || (isAr ? 'ملغى' : 'Annulé') }
        ].map(s => (
          <button 
            key={s.v} 
            onClick={() => setFilter(s.v)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors shrink-0 ${
              filter === s.v 
                ? 'bg-blue-600 text-white shadow-sm' 
                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {s.l}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm table-container">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left w-10">
                <input 
                  type="checkbox" 
                  checked={filtered.length > 0 && selectedRentalIds.length === filtered.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRentalIds(filtered.map(r => r.id));
                    } else {
                      setSelectedRentalIds([]);
                    }
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer h-4 w-4"
                />
              </th>
              {[
                { key: 'col_client', l: t('col_client') },
                { key: 'col_car', l: t('col_car') },
                { key: 'col_from', l: t('col_from') },
                { key: 'col_to', l: t('col_to') },
                { key: 'col_price', l: t('col_price') },
                { key: 'col_status', l: t('col_status') },
                { key: 'col_documents', l: t('col_documents') || (isAr ? 'الوثائق' : 'Documents') }
              ].map(th => (
                <th 
                  key={th.key} 
                  className={`${isAr ? 'text-right' : 'text-left'} px-4 py-3 text-xs font-bold text-gray-600 uppercase tracking-wide`}
                >
                  {th.l}
                </th>
              ))}
              <th className="text-center px-4 py-3 text-xs font-bold text-gray-600 uppercase tracking-wide">
                {t('col_contract')}
              </th>
              <th className={`${isAr ? 'text-left' : 'text-right'} px-4 py-3 text-xs font-bold text-gray-600 uppercase tracking-wide`}>
                {t('col_actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(r => (
              <tr key={r.id} className="hover:bg-gray-50/80 transition-colors">
                <td className="px-4 py-4 w-10">
                  <input 
                    type="checkbox" 
                    checked={selectedRentalIds.includes(r.id)}
                    onChange={() => {
                      setSelectedRentalIds(prev => 
                        prev.includes(r.id) ? prev.filter(id => id !== r.id) : [...prev, r.id]
                      );
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer h-4 w-4"
                  />
                </td>
                <td className="px-4 py-4 font-bold text-gray-900">{r.clientName}</td>
                <td className="px-4 py-4 text-sm text-gray-650 text-gray-600">{r.carName}</td>
                <td className="px-4 py-4 text-sm text-gray-500 font-mono">{formatDateFR(r.startDate)}</td>
                <td className="px-4 py-4 text-sm text-gray-500">
                  {r.isOpenContract ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-bold border border-purple-100 animate-pulse">
                      <Clock className="w-3.5 h-3.5" />
                      {isAr ? 'مفتوح ⏳' : 'Ouvert ⏳'}
                    </span>
                  ) : (
                    <span className="font-mono">{formatDateFR(r.endDate) || '—'}</span>
                  )}
                </td>
                <td className="px-4 py-4 text-sm font-bold text-gray-900">
                  <div className="flex items-center gap-1.5 font-mono">
                    <span>{`${r.totalAmount} ${settings.currency}`}</span>
                    {r.isOpenContract && r.status === 'active' && (
                      <span 
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-purple-50 text-purple-650 rounded text-[10px] font-bold border border-purple-150"
                        title={isAr ? `${r.days} يوم منذ الانطلاق` : `${r.days} jours depuis le début`}
                      >
                        ⚡{r.days}j
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex flex-col gap-1.5 max-w-[120px]">
                    <select 
                      value={r.status} 
                      onChange={e => handleStatusChange(r.id, e.target.value)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border border-transparent shadow-sm cursor-pointer ${
                        r.status === 'active' ? 'bg-green-50 text-green-700 border-green-100' :
                        r.status === 'completed' ? 'bg-gray-100 text-gray-750 border-gray-200' : 'bg-red-50 text-red-700 border-red-100'
                      }`}
                    >
                      <option value="active">{t('status_active')}</option>
                      <option value="completed">{t('status_completed')}</option>
                      <option value="cancelled">{t('status_cancelled')}</option>
                    </select>
                    
                    {r.status === 'completed' && (() => {
                      const client = clients.find(c => c.id === r.clientId);
                      const clientCredit = client?.creditBalance || 0;
                      if (clientCredit > 0) {
                        return (
                          <span 
                            className="inline-flex items-center justify-center gap-1 px-2 py-1 bg-red-50 text-red-800 rounded-lg text-[10px] font-bold border border-red-200 animate-pulse"
                            title={isAr ? `العميل عليه دين: ${clientCredit} MAD` : `Client doit: ${clientCredit} MAD`}
                          >
                            💰 {isAr ? 'دين' : 'Crédit'}
                          </span>
                        );
                      } else if (clientCredit < 0) {
                        return (
                          <span 
                            className="inline-flex items-center justify-center gap-1 px-2 py-1 bg-blue-50 text-blue-800 rounded-lg text-[10px] font-bold border border-blue-200"
                            title={isAr ? 'العميل عنده رصيد مسبق' : 'Client a une avance'}
                          >
                            ✨ {isAr ? 'مسبق' : 'Avance'}
                          </span>
                        );
                      } else {
                        return (
                          <span className="inline-flex items-center justify-center gap-1 px-2 py-1 bg-green-50 text-green-800 rounded-lg text-[10px] font-bold border border-green-200">
                            ✅ {isAr ? 'مدفوع' : 'Payé'}
                          </span>
                        );
                      }
                    })()}
                  </div>
                </td>
                <td className="px-4 py-4">
                  {(() => {
                    const client = clients.find(c => c.id === r.clientId);
                    const cniRecto = r.clientDocCniRecto || client?.docCniRecto || client?.docCni || '';
                    const cniVerso = r.clientDocCniVerso || client?.docCniVerso || '';
                    const permisRecto = r.clientDocPermisRecto || client?.docPermisRecto || client?.docPermis || '';
                    const permisVerso = r.clientDocPermisVerso || client?.docPermisVerso || '';
                    const passport = r.clientDocPassport || client?.docPassport || '';

                    const hasDocs = cniRecto || cniVerso || permisRecto || permisVerso || passport;

                    if (!hasDocs) {
                      return <span className="text-gray-400 text-xs font-bold">—</span>;
                    }

                    return (
                      <div className="flex gap-1.5 items-center">
                        {cniRecto && (
                          <button 
                            onClick={() => setViewDoc({ url: cniRecto, title: (isAr ? 'CIN الوجه' : 'CIN Recto') })} 
                            className="p-1.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 shadow-sm transition border border-emerald-100" 
                            title={isAr ? 'بطاقة التعريف - الوجه' : 'CIN Recto'}
                          >
                            <FileBadge className="w-4 h-4" />
                          </button>
                        )}
                        {cniVerso && (
                          <button 
                            onClick={() => setViewDoc({ url: cniVerso, title: (isAr ? 'CIN الخلف' : 'CIN Verso') })} 
                            className="p-1.5 bg-emerald-100 text-emerald-800 rounded-lg hover:bg-emerald-200 shadow-sm transition border border-emerald-200" 
                            title={isAr ? 'بطاقة التعريف - الخلف' : 'CIN Verso'}
                          >
                            <FileBadge className="w-4 h-4" />
                          </button>
                        )}
                        {permisRecto && (
                          <button 
                            onClick={() => setViewDoc({ url: permisRecto, title: (isAr ? 'الرخصة الوجه' : 'Permis Recto') })} 
                            className="p-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 shadow-sm transition border border-blue-100" 
                            title={isAr ? 'رخصة السياقة - الوجه' : 'Permis Recto'}
                          >
                            <Car className="w-4 h-4" />
                          </button>
                        )}
                        {permisVerso && (
                          <button 
                            onClick={() => setViewDoc({ url: permisVerso, title: (isAr ? 'الرخصة الخلف' : 'Permis Verso') })} 
                            className="p-1.5 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 shadow-sm transition border border-blue-200" 
                            title={isAr ? 'رخصة السياقة - الخلف' : 'Permis Verso'}
                          >
                            <Car className="w-4 h-4" />
                          </button>
                        )}
                        {passport && (
                          <button 
                            onClick={() => setViewDoc({ url: passport, title: isAr ? 'جواز السفر' : 'Passeport' })} 
                            className="p-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 shadow-sm transition border border-purple-100" 
                            title={isAr ? 'جواز السفر' : 'Passeport'}
                          >
                            <Globe className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-4 py-4 text-center">
                  <div className="flex gap-1.5 justify-center flex-wrap">
                    <button
                      onClick={() => printContract(r)}
                      className="p-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-750 rounded-lg text-xs font-bold transition shadow-sm border border-indigo-100"
                      title={t('print_btn')}
                    >
                      🖨️
                    </button>
                    <button
                      onClick={() => printContract(r, 'print_rectoverso')}
                      className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-bold transition shadow-sm border border-blue-100"
                      title={isAr ? 'طباعة العقد وجهان (Recto-Verso) مع معاينة' : 'Imprimer le contrat Recto-Verso (aperçu)'}
                    >
                      🔁
                    </button>
                    <button 
                      onClick={() => {
                        setDownloadDocsState({
                          rentals: [r]
                        });
                      }}
                      className="p-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition shadow-sm border border-green-100 flex items-center justify-center hover:scale-105"
                      title={isAr ? 'تحميل العقد والوثائق المحدد' : 'Télécharger contrat & documents'}
                    >
                      <Download className="w-4 h-4 text-green-600" />
                    </button>
                    <button
                      onClick={() => setSignShareState(r)}
                      className={`p-2 rounded-lg text-xs font-bold transition shadow-sm border ${
                        r.clientSignature 
                          ? 'bg-green-50 text-green-700 border-green-150 hover:bg-green-100' 
                          : 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600'
                      }`}
                      title={r.clientSignature ? (isAr ? 'تم التوقيع - إعادة الإرسال' : 'Signé - Renvoyer') : (isAr ? 'إرسال رابط التوقيع' : 'Envoyer lien signature')}
                    >
                      {r.clientSignature ? '✍️ ✓' : '✍️'}
                    </button>
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <button 
                    onClick={() => handleDelete(r.id)}
                    className="text-red-650 hover:bg-red-50 p-2 rounded-lg transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-10 text-gray-500 font-medium bg-white rounded-xl border border-gray-150 border-dashed">
                  {t('no_rentals_found') || (isAr ? 'لا توجد عقود كراء حالياً' : 'Aucune location enregistrée')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <RentalForm 
          clients={clients} 
          cars={cars} 
          settings={settings} 
          onSave={handleSave} 
          onClose={() => setShowForm(false)} 
        />
      )}

      {viewDoc && (
        <DocumentViewerModal
          url={viewDoc.url}
          title={viewDoc.title}
          onClose={() => setViewDoc(null)}
        />
      )}

      {downloadDocsState && (
        <DownloadDocsModal
          rentals={downloadDocsState.rentals}
          printContract={printContract}
          isAr={isAr}
          viewDocCallback={setViewDoc}
          onClose={() => {
            setDownloadDocsState(null);
            setSelectedRentalIds([]);
          }}
          settings={settings}
          clients={clients}
          cars={cars}
        />
      )}

      {signShareState && (
        <SignShareModal
          rental={signShareState}
          settings={settings}
          isAr={isAr}
          onClose={() => setSignShareState(null)}
        />
      )}

      {selectedRentalIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] bg-white/95 backdrop-blur-md border border-gray-200 px-6 py-4 rounded-2xl shadow-xl flex items-center gap-6 animate-slide-up">
          <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-xs text-blue-600 font-extrabold">
              {selectedRentalIds.length}
            </span>
            <span>
              {isAr ? 'عقود محددة' : (selectedRentalIds.length === 1 ? 'contrat sélectionné' : 'contrats sélectionnés')}
            </span>
          </div>
          
          <div className="h-6 w-[1px] bg-gray-200" />
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setDownloadDocsState({
                  rentals: filtered.filter(r => selectedRentalIds.includes(r.id))
                });
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl text-xs font-bold transition-all border border-green-150 shadow-sm hover:scale-105"
            >
              <Download className="w-3.5 h-3.5" />
              {isAr ? 'تحميل (ZIP)' : 'Télécharger ZIP'}
            </button>
            
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl text-xs font-bold transition-all border border-red-150 shadow-sm hover:scale-105"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {isAr ? 'حذف المحدد' : 'Supprimer la sélection'}
            </button>
            
            <button
              onClick={() => setSelectedRentalIds([])}
              className="px-3 py-2 text-xs font-semibold text-gray-500 hover:text-gray-750 hover:bg-gray-50 rounded-xl transition"
            >
              {isAr ? 'إلغاء' : 'Annuler'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SignShareModal({ rental: r, settings, isAr, onClose }) {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [qrError, setQrError] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);

  const baseUrl = window.location.origin + window.location.pathname;
  const signUrl = `${baseUrl}?sign=${r.id}&agency=${window.__AGENCY_ID__ || ''}`;

  const message = isAr
    ? `السلام عليكم ${r.clientName}،\n\n` +
      `يرجى توقيع عقد كراء السيارة:\n` +
      `🚗 ${r.carName}\n` +
      `📅 ${r.startDate}\n` +
      `💰 ${r.totalAmount} ${settings.currency}\n\n` +
      `اضغط على الرابط للتوقيع:\n${signUrl}\n\n` +
      `شكراً - ${settings.companyName || 'Auto Services'}`
    : `Bonjour ${r.clientName},\n\n` +
      `Veuillez signer votre contrat de location:\n` +
      `🚗 ${r.carName}\n` +
      `📅 Du ${r.startDate}${r.endDate ? ' au ' + r.endDate : ''}\n` +
      `💰 Montant: ${r.totalAmount} ${settings.currency}\n\n` +
      `Cliquez ici pour signer:\n${signUrl}\n\n` +
      `Merci - ${settings.companyName || 'Auto Services'}`;

  let phone = (r.clientPhone || '').replace(/\s+/g, '').replace(/[^\d+]/g, '');
  if (phone.startsWith('0')) phone = '212' + phone.slice(1);
  if (phone.startsWith('+')) phone = phone.slice(1);
  const waUrl = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;

  // Generate QR code dynamically for this contract
  useEffect(() => {
    let active = true;
    QRCode.toDataURL(signUrl, { width: 320, margin: 2, errorCorrectionLevel: 'M' })
      .then((url) => { if (active) setQrDataUrl(url); })
      .catch(() => { if (active) setQrError(true); });
    return () => { active = false; };
  }, [signUrl]);

  // Lock body scroll while open
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(signUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      window.prompt(isAr ? 'انسخ الرابط:' : 'Copiez le lien:', signUrl);
    });
  };

  const handleWhatsApp = () => {
    window.open(waUrl, '_blank');
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-[10000] sm:p-4"
      onClick={onClose}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div
        className="bg-white w-full sm:rounded-2xl rounded-t-2xl shadow-2xl sm:max-w-md max-h-[92vh] flex flex-col overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-4 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h2 className="font-bold text-base text-white truncate">
              {isAr ? 'مشاركة رابط التوقيع' : 'Partager le lien de signature'}
            </h2>
            <p className="text-emerald-50 text-xs truncate mt-0.5">
              {r.clientName} · {r.carName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 active:bg-white/30 rounded-lg transition-colors shrink-0"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto overscroll-contain">
          {!showQr ? (
            <div className="space-y-3">
              {/* Copy Link */}
              <button
                onClick={handleCopy}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/60 active:scale-[0.99] transition-all text-start"
              >
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl shrink-0 transition-colors ${copied ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                  {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
                </span>
                <span className="min-w-0">
                  <span className="block font-bold text-gray-900 text-sm">
                    {copied ? (isAr ? 'تم نسخ الرابط ✓' : 'Lien copié ✓') : (isAr ? 'نسخ الرابط' : 'Copier le lien')}
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {isAr ? 'انسخه إلى الحافظة' : 'Copier dans le presse-papiers'}
                  </span>
                </span>
              </button>

              {/* WhatsApp */}
              <button
                onClick={handleWhatsApp}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/60 active:scale-[0.99] transition-all text-start"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </span>
                <span className="min-w-0">
                  <span className="block font-bold text-gray-900 text-sm">
                    {isAr ? 'إرسال عبر واتساب' : 'Envoyer via WhatsApp'}
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5 truncate">
                    {r.clientPhone
                      ? r.clientPhone
                      : (isAr ? 'بدون رقم — اختر جهة الاتصال' : 'Sans numéro — choisir le contact')}
                  </span>
                </span>
              </button>

              {/* View QR */}
              <button
                onClick={() => setShowQr(true)}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-purple-300 hover:bg-purple-50/60 active:scale-[0.99] transition-all text-start"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-100 text-purple-600 shrink-0">
                  <QrCode className="w-5 h-5" />
                </span>
                <span className="min-w-0">
                  <span className="block font-bold text-gray-900 text-sm">
                    {isAr ? 'عرض رمز QR' : 'Afficher le QR Code'}
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {isAr ? 'مسح للوصول إلى صفحة التوقيع' : 'Scanner pour ouvrir la signature'}
                  </span>
                </span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center text-center">
              <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm">
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt="QR Code"
                    className="w-56 h-56 sm:w-64 sm:h-64"
                  />
                ) : qrError ? (
                  <div className="w-56 h-56 sm:w-64 sm:h-64 flex items-center justify-center text-sm text-red-500 px-4">
                    {isAr ? 'تعذر إنشاء رمز QR' : 'Impossible de générer le QR'}
                  </div>
                ) : (
                  <div className="w-56 h-56 sm:w-64 sm:h-64 flex items-center justify-center text-sm text-gray-400">
                    {isAr ? 'جاري الإنشاء…' : 'Génération…'}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-4 max-w-xs">
                {isAr
                  ? 'وجّه كاميرا هاتف الزبون نحو الرمز لفتح صفحة توقيع هذا العقد.'
                  : 'Pointez la caméra du client vers ce code pour ouvrir la signature de ce contrat.'}
              </p>
              <button
                onClick={() => setShowQr(false)}
                className="mt-5 px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold transition-colors"
              >
                {isAr ? '← رجوع' : '← Retour'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function DownloadDocsModal({ rentals, printContract, onClose, isAr, viewDocCallback, settings, clients, cars }) {
  const [selectedRentalIds, setSelectedRentalIds] = useState(rentals.map(r => r.id));
  const [downloading, setDownloading] = useState(false);
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [statusText, setStatusText] = useState('');

  const getFileInfo = (url, contentType) => {
    // Prefer Content-Type header (most reliable, especially for Supabase signed URLs
    // where the path has no file extension or has a UUID-style identifier)
    if (contentType) {
      const mime = contentType.split(';')[0].trim().toLowerCase();
      const mimeToExt = {
        'application/pdf': 'pdf',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
        'image/webp': 'webp',
        'image/gif': 'gif',
        'image/bmp': 'bmp',
        'image/tiff': 'tiff',
        'image/svg+xml': 'svg',
      };
      if (mimeToExt[mime]) return { ext: mimeToExt[mime] };
    }
    // Fallback: detect from URL path
    if (!url) return { ext: 'bin' };
    if (url.startsWith('data:')) {
      const match = url.match(/^data:([^;]+);base64,/);
      const mime = match ? match[1] : 'image/png';
      const ext = mime.split('/')[1] || 'png';
      return { ext: ext === 'jpeg' ? 'jpg' : ext };
    } else {
      const cleanUrl = url.split(/[?#]/)[0];
      const urlExt = cleanUrl.split('.').pop().toLowerCase();
      // Only trust extension if short and looks like a real file extension
      const ext = /^[a-z0-9]{2,5}$/.test(urlExt) ? urlExt : 'bin';
      return { ext };
    }
  };

  const toggleSelect = (id) => {
    if (downloading) return;
    setSelectedRentalIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (downloading) return;
    if (selectedRentalIds.length === rentals.length) {
      setSelectedRentalIds([]);
    } else {
      setSelectedRentalIds(rentals.map(r => r.id));
    }
  };
  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setStatus('loading');
    setStatusText(isAr ? 'جاري التحميل...' : 'Téléchargement en cours...');
    
    const rentalsToDownload = rentals.filter(r => selectedRentalIds.includes(r.id));
    
    const zip = new JSZip();
    let errors = [];
    let errorOccurred = false;

    for (let i = 0; i < rentalsToDownload.length; i++) {
      const r = rentalsToDownload[i];
      const folderName = `Dossier_${r.clientName}_${r.carName}_${r.startDate}`.replace(/[^a-zA-Z0-9\-_]/g, '_').replace(/__+/g, '_');
      
      setStatusText(isAr ? `جاري معالجة ملف: ${r.clientName}` : `Traitement du dossier : ${r.clientName}`);
      
      const client = clients.find(c => c.id === r.clientId);
      const car = cars.find(c => c.id === r.carId);

      // 1. Contract Recto/Verso HTML (Pages 1 & 2)
      try {
        if (printContract) {
          const html = await printContract(r, 'get_contract_html');
          if (html) {
            zip.file(`${folderName}/Contract.html`, html);
          } else {
            throw new Error("Failed to generate Contract HTML");
          }
        }
      } catch (err) {
        console.error('Contract HTML failed for', r.clientName, err);
        errors.push(`${r.clientName} (Contract.html): ${err.message}`);
        errorOccurred = true;
      }

      // 2. Commitment HTML (Page 3)
      try {
        if (printContract) {
          const html = await printContract(r, 'get_commitment_html');
          if (html) {
            zip.file(`${folderName}/Commitment.html`, html);
          } else {
            throw new Error("Failed to generate Commitment HTML");
          }
        }
      } catch (err) {
        console.error('Commitment HTML failed for', r.clientName, err);
        errors.push(`${r.clientName} (Commitment.html): ${err.message}`);
        errorOccurred = true;
      }

      // 3. Violation HTML (Page 4 / NARSA)
      try {
        if (printContract) {
          const html = await printContract(r, 'get_narsa_html');
          if (html) {
            zip.file(`${folderName}/Violation.html`, html);
          } else {
            throw new Error("Failed to generate NARSA Violation HTML");
          }
        }
      } catch (err) {
        console.error('Violation HTML failed for', r.clientName, err);
        errors.push(`${r.clientName} (Violation.html): ${err.message}`);
        errorOccurred = true;
      }

      // 4. National ID (CIN Recto/Verso/Passport)
      const cinDocs = [
        { key: 'CIN_Recto', val: r.clientDocCniRecto || client?.docCniRecto || client?.docCni || '' },
        { key: 'CIN_Verso', val: r.clientDocCniVerso || client?.docCniVerso || '' },
        { key: 'Passport', val: r.clientDocPassport || client?.docPassport || '' }
      ].filter(d => d.val);

      for (const d of cinDocs) {
        try {
          const response = await fetch(d.val);
          if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);
          const blob = await response.blob();
          const contentType = response.headers.get('Content-Type');
          const fileInfo = getFileInfo(d.val, contentType);
          zip.file(`${folderName}/${d.key}.${fileInfo.ext}`, blob, { binary: true });
        } catch (err) {
          console.error(`CIN Doc fetch failed for ${d.key}`, err);
          errors.push(`${r.clientName} (${d.key}): ${err.message}`);
          errorOccurred = true;
        }
      }

      // 5. Driver License Recto/Verso
      const licenseDocs = [
        { key: 'Driver_License_Recto', val: r.clientDocPermisRecto || client?.docPermisRecto || client?.docPermis || '' },
        { key: 'Driver_License_Verso', val: r.clientDocPermisVerso || client?.docPermisVerso || '' }
      ].filter(d => d.val);

      for (const d of licenseDocs) {
        try {
          const response = await fetch(d.val);
          if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);
          const blob = await response.blob();
          const contentType = response.headers.get('Content-Type');
          const fileInfo = getFileInfo(d.val, contentType);
          zip.file(`${folderName}/${d.key}.${fileInfo.ext}`, blob, { binary: true });
        } catch (err) {
          console.error(`License Doc fetch failed for ${d.key}`, err);
          errors.push(`${r.clientName} (${d.key}): ${err.message}`);
          errorOccurred = true;
        }
      }

      // 6. Vehicle Photo
      if (car?.photo) {
        try {
          const response = await fetch(car.photo);
          if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);
          const blob = await response.blob();
          const contentType = response.headers.get('Content-Type');
          const fileInfo = getFileInfo(car.photo, contentType);
          zip.file(`${folderName}/Vehicle_Photo_1.${fileInfo.ext}`, blob, { binary: true });
        } catch (err) {
          console.error('Vehicle Photo fetch failed', err);
          errors.push(`${r.clientName} (Vehicle_Photo_1): ${err.message}`);
          errorOccurred = true;
        }
      }

      if (rentalsToDownload.length > 1) {
        await new Promise(resolve => setTimeout(resolve, 150));
      }
    }

    if (errors.length > 0) {
      zip.file("error_log.txt", "Certains documents n'ont pas pu être téléchargés :\n\n" + errors.join("\n"));
    }

    try {
      setStatusText(isAr ? 'جاري إنشاء ملف ZIP...' : 'Création de l\'archive ZIP...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const blobUrl = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const zipName = rentalsToDownload.length === 1 
        ? `Dossier_${rentalsToDownload[0].clientName.replace(/\s+/g, '_')}.zip` 
        : `Dossiers_Locations.zip`;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);

      if (errorOccurred) {
        setStatus('error');
        setStatusText(isAr ? 'تم تحميل الأرشيف مع وجود بعض الأخطاء (راجع error_log.txt).' : 'Archive téléchargée avec des erreurs (voir error_log.txt).');
      } else {
        setStatus('success');
        setStatusText(isAr ? 'تم تحميل ملف ZIP بنجاح!' : 'Archive ZIP téléchargée avec succès !');
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (zipErr) {
      console.error('Failed to create ZIP', zipErr);
      setStatus('error');
      setStatusText(isAr ? 'فشل إنشاء ملف ZIP.' : 'Échec de création du ZIP.');
    }
    
    setDownloading(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[999990] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
      <div className="relative w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl border border-gray-100 flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
          <div>
            <h3 className="font-bold text-gray-950 text-lg">
              {isAr ? 'تحميل أرشيف الوثائق' : 'Télécharger les documents (ZIP)'}
            </h3>
            <p className="text-xs text-gray-550 mt-0.5 font-medium">
              {rentals.length === 1 
                ? rentals[0].clientName 
                : (isAr ? `${rentals.length} ملفات محددة` : `${rentals.length} dossiers sélectionnés`)}
            </p>
          </div>
          <button 
            onClick={onClose} 
            disabled={downloading}
            className="p-1.5 text-gray-455 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status Alert Banner */}
        {status === 'success' && (
          <div className="mx-5 mt-4 p-3 bg-emerald-50 text-emerald-800 rounded-xl text-xs font-bold flex items-center gap-2 border border-emerald-200 animate-slide-in">
            <span className="text-sm">✅</span>
            <span>{statusText}</span>
          </div>
        )}
        {status === 'error' && (
          <div className="mx-5 mt-4 p-3 bg-red-50 text-red-800 rounded-xl text-xs font-bold flex items-center gap-2 border border-red-200 animate-slide-in">
            <span className="text-sm">❌</span>
            <span>{statusText}</span>
          </div>
        )}
        {status === 'loading' && (
          <div className="mx-5 mt-4 p-3 bg-blue-50 text-blue-800 rounded-xl text-xs font-bold flex items-center gap-2 border border-blue-200 animate-slide-in">
            <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-blue-800 border-t-transparent shrink-0" />
            <span>{statusText}</span>
          </div>
        )}

        {/* Body */}
        <div className="p-5 flex-1 overflow-auto max-h-[60vh]">
          {/* Select All */}
          <button 
            onClick={toggleSelectAll}
            disabled={downloading}
            className="flex items-center gap-3 w-full p-3 hover:bg-gray-50 rounded-xl transition text-left text-sm font-bold text-gray-700 mb-4 border border-dashed border-gray-200 disabled:opacity-50"
          >
            {selectedRentalIds.length === rentals.length ? (
              <CheckSquare className="w-5 h-5 text-green-600 shrink-0" />
            ) : (
              <Square className="w-5 h-5 text-gray-400 shrink-0" />
            )}
            <span>
              {selectedRentalIds.length === rentals.length 
                ? (isAr ? 'إلغاء تحديد الكل' : 'Désélectionner tout') 
                : (isAr ? 'تحديد الكل' : 'Sélectionner tout')}
            </span>
          </button>

          {/* List of records */}
          <div className="space-y-2">
            {rentals.map(r => {
              const isSelected = selectedRentalIds.includes(r.id);
              return (
                <div 
                  key={r.id}
                  className={`flex items-center justify-between p-3.5 rounded-xl border transition-all duration-200 ${
                    isSelected ? 'border-green-200 bg-green-50/10' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <button 
                    onClick={() => toggleSelect(r.id)}
                    disabled={downloading}
                    className="flex items-center gap-3 flex-1 text-left disabled:opacity-80"
                  >
                    {isSelected ? (
                      <CheckSquare className="w-5 h-5 text-green-600 shrink-0" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400 shrink-0" />
                    )}
                    <div>
                      <span className="text-sm font-bold text-gray-900 flex items-center gap-1.5 font-mono">
                        📂 {r.clientName}
                      </span>
                      <span className="text-[10px] text-gray-500 font-mono block text-left">
                        {r.carName} — {formatDateFR(r.startDate)}
                      </span>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-3">
          <button 
            onClick={onClose} 
            disabled={downloading}
            className="flex-1 py-2.5 border border-gray-250 text-gray-700 hover:bg-gray-150 rounded-xl text-sm font-bold transition disabled:opacity-50"
          >
            {isAr ? 'إلغاء' : 'Annuler'}
          </button>
          <button 
            onClick={handleDownload}
            disabled={selectedRentalIds.length === 0 || downloading}
            className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            <Download className="w-4 h-4" />
            {downloading 
              ? (isAr ? 'جاري التحميل...' : 'Téléchargement...') 
              : (isAr ? 'تحميل المحدد (ZIP)' : 'Télécharger ZIP')}
            {selectedRentalIds.length > 0 && ` (${selectedRentalIds.length})`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
